/**
 * Lead search — the kind people expect from a CRM.
 *
 * Typing a BDA's name, a POC's email, a phone number, a country or part of a
 * website should all find the lead. Multiple words narrow the result (every
 * word must appear somewhere), so "solar india" finds Indian solar companies.
 * A quoted "exact phrase" matches as one string, and a leading - excludes.
 */

const FIELDS = [
  'company_name', 'company_url', 'linkedin_url', 'core_business', 'lead_source',
  'custom_lead_source', 'stage', 'priority', 'interest_status', 'bde_name',
  'country', 'city', 'state', 'location', 'company_generic_number',
  'poc_name', 'poc_designation', 'email', 'poc_email', 'contact_no',
  'poc2_name', 'poc2_designation', 'poc2_email', 'poc2_contact_no',
  'poc3_name', 'poc3_designation', 'poc3_email', 'poc3_contact_no',
  'poc4_name', 'poc4_designation', 'poc4_email', 'poc4_contact_no',
  'company_overview', 'contact_book', 'notes',
  'follow_up_1_update', 'follow_up_2_update', 'follow_up_3_update'
];

/**
 * Everything about a lead, lowercased, as one string to scan.
 *
 * Cached against the lead object itself: the list is fetched once and filtered
 * on every keystroke, so rebuilding ~35 fields per lead per character was
 * needless work. A WeakMap means a lead that leaves the list takes its cache
 * with it, and re-fetching produces new objects and therefore fresh entries.
 */
const HAYSTACKS = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

export const leadHaystack = (lead) => {
  if (!lead) return '';
  if (HAYSTACKS && typeof lead === 'object') {
    const cached = HAYSTACKS.get(lead);
    if (cached !== undefined) return cached;
  }
  let out = '';
  for (const field of FIELDS) {
    const value = lead[field];
    if (value == null || value === '') continue;
    out += String(value).toLowerCase() + ' ';
  }
  // Digits only, so "+44 203 996 9173" is found by "2039969173"
  const digits = out.replace(/[^0-9]/g, '');
  const haystack = digits ? `${out} ${digits}` : out;
  if (HAYSTACKS && typeof lead === 'object') HAYSTACKS.set(lead, haystack);
  return haystack;
};

/** Split a query into terms, honouring "quoted phrases" and -exclusions. */
export const parseQuery = (query) => {
  const terms = [];
  const excluded = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m;
  while ((m = re.exec(query || '')) !== null) {
    const raw = (m[1] || m[2] || '').trim().toLowerCase();
    if (!raw) continue;
    if (raw.startsWith('-') && raw.length > 1) excluded.push(raw.slice(1));
    else terms.push(raw);
  }
  return { terms, excluded };
};

/**
 * Does this lead match the query? Every term must appear (AND), and any
 * -excluded term disqualifies it. An empty query matches everything.
 */
export const matchesLeadSearch = (lead, query) => {
  const trimmed = (query || '').trim();
  if (!trimmed) return true;
  const { terms, excluded } = parseQuery(trimmed);
  if (terms.length === 0 && excluded.length === 0) return true;
  const hay = leadHaystack(lead);
  // A phone search should still work when the user types the spaces in.
  const digitsOfQuery = trimmed.replace(/[^0-9]/g, '');
  if (digitsOfQuery.length >= 6 && hay.includes(digitsOfQuery)) return true;
  for (const term of excluded) if (hay.includes(term)) return false;
  for (const term of terms) if (!hay.includes(term)) return false;
  return true;
};

/** Distinct non-empty values of a field, trimmed and sorted — for filter dropdowns. */
export const distinctValues = (rows, field) => {
  const seen = new Map();
  (rows || []).forEach(row => {
    const raw = row && row[field];
    if (raw == null) return;
    const value = String(raw).trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (!seen.has(key)) seen.set(key, value);
  });
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};
